import React, { useState, useEffect } from 'react';
import { useRazorpay } from 'react-razorpay';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, TrendingUp, Users, Globe, Star, Zap, Shield, Tag, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

const LinkInsertionServices = () => {
  const { Razorpay } = useRazorpay();
  
  // Set page title for SEO
  useEffect(() => {
    document.title = 'Quality Backlinks from iTMunch | High Authority DA 68 DoFollow Links';
  }, []);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    url: '',
    itmunch_article: '',
    article_content: '',
    article_file: null,
    link1: '',
    link2: '',
    link3: '',
    custom_request: ''
  });
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [discountedPrice, setDiscountedPrice] = useState(null);

  const packages = [
    {
      id: 'single_link',
      title: 'Single Link Insertion',
      originalPrice: 30,
      price: 15,
      discount: '50% OFF',
      features: [
        'One high-quality backlink',
        'DoFollow link',
        'Permanent placement',
        'Editorial review',
        'Traffic boost'
      ],
      popular: false
    },
    {
      id: 'article_three_links',
      title: 'Article with 3 Links',
      originalPrice: 60,
      price: 30,
      discount: '50% OFF',
      features: [
        'Full article submission',
        'Three DoFollow links',
        'SEO optimized content',
        'Permanent placement',
        'Social media promotion',
        'Analytics report'
      ],
      popular: true
    },
    {
      id: 'custom',
      title: 'Custom Request',
      originalPrice: null,
      price: 'Contact',
      discount: 'Best Value',
      features: [
        'Tailored solutions',
        'Multiple articles',
        'Bulk link insertion',
        'Priority support',
        'Custom analytics',
        'Dedicated account manager'
      ],
      popular: false
    }
  ];

  const handlePackageSelect = (pkg) => {
    // Reset form when switching packages
    setFormData({
      name: '',
      email: '',
      phone: '',
      url: '',
      itmunch_article: '',
      article_content: '',
      article_file: null,
      link1: '',
      link2: '',
      link3: '',
      custom_request: ''
    });
    // Reset coupon state when switching packages
    setCouponCode('');
    setCouponApplied(null);
    setDiscountedPrice(null);
    setSelectedPackage(pkg);
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('form-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    try {
      setCouponLoading(true);
      const response = await axios.post(`${API}/validate-coupon`, {
        code: couponCode.trim(),
        package_type: selectedPackage.id,
        original_amount: selectedPackage.price * 100 // Convert to cents
      });

      if (response.data.valid) {
        setCouponApplied(response.data);
        setDiscountedPrice(response.data.final_amount / 100); // Convert back to dollars
        toast.success(response.data.message);
      }
    } catch (err) {
      console.error('Coupon error:', err);
      setCouponApplied(null);
      setDiscountedPrice(null);
      toast.error(err.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
    setDiscountedPrice(null);
    toast.info('Coupon removed');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        article_file: file
      });
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (selectedPackage.id === 'single_link' && (!formData.url || !formData.itmunch_article)) {
      toast.error('Please provide both the iTMunch article URL and your link URL');
      return;
    }

    if (selectedPackage.id === 'article_three_links' && (!formData.article_content && !formData.article_file)) {
      toast.error('Please provide article content or upload an article file');
      return;
    }

    if (selectedPackage.id === 'article_three_links' && (!formData.link1 || !formData.link2 || !formData.link3)) {
      toast.error('Please provide all 3 links for the article');
      return;
    }

    if (selectedPackage.id === 'custom' && !formData.custom_request) {
      toast.error('Please describe your custom request');
      return;
    }

    try {
      setLoading(true);
      
      // For custom package, submit using FormData
      if (selectedPackage.id === 'custom') {
        const formDataToSend = new FormData();
        formDataToSend.append('amount', 0);
        formDataToSend.append('currency', 'USD');
        formDataToSend.append('package_type', selectedPackage.id);
        formDataToSend.append('name', formData.name);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('phone', formData.phone);
        formDataToSend.append('custom_request', formData.custom_request);

        await axios.post(`${API}/create-order`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        toast.success('Custom request submitted! We will contact you shortly.');
        setShowForm(false);
        setSelectedPackage(null);
        setFormData({ name: '', email: '', phone: '', url: '', itmunch_article: '', article_content: '', article_file: null, link1: '', link2: '', link3: '', custom_request: '' });
        setLoading(false);
        return;
      }

      // Create order with FormData for file upload
      const formDataToSend = new FormData();
      // Always send ORIGINAL price - backend will apply coupon discount
      formDataToSend.append('amount', selectedPackage.price * 100);
      formDataToSend.append('currency', 'USD');
      formDataToSend.append('package_type', selectedPackage.id);
      // Include coupon code if applied - backend will validate and apply discount
      if (couponApplied) {
        formDataToSend.append('coupon_code', couponApplied.code);
      }
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      
      if (formData.url) formDataToSend.append('url', formData.url);
      if (formData.itmunch_article) formDataToSend.append('itmunch_article', formData.itmunch_article);
      if (formData.article_content) formDataToSend.append('article_content', formData.article_content);
      if (formData.article_file) formDataToSend.append('article_file', formData.article_file);
      if (formData.link1) formDataToSend.append('link1', formData.link1);
      if (formData.link2) formDataToSend.append('link2', formData.link2);
      if (formData.link3) formDataToSend.append('link3', formData.link3);

      const orderResponse = await axios.post(`${API}/create-order`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const { order_id, amount, currency, submission_id } = orderResponse.data;

      // Razorpay options
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: amount,
        currency: currency,
        name: 'iTMunch',
        description: selectedPackage.title,
        order_id: order_id,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(`${API}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              submission_id: submission_id
            });
            
            toast.success('Payment successful! We will process your request shortly.');
            setShowForm(false);
            setSelectedPackage(null);
            setFormData({ name: '', email: '', phone: '', url: '', itmunch_article: '', article_content: '', article_file: null, link1: '', link2: '', link3: '', custom_request: '' });
            // Reset coupon state
            setCouponCode('');
            setCouponApplied(null);
            setDiscountedPrice(null);
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#4AC5DB'
        }
      };

      const rzp = new Razorpay(options);
      rzp.on('payment.failed', function (response) {
        toast.error('Payment failed. Please try again.');
      });
      rzp.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-teal-50">
      {/* Navigation Header */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="https://itmunch.com/" target="_blank" rel="noopener noreferrer" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_editorial-connect/artifacts/r8fs74mn_iTMunch%20B2B%20Content%20Syndication%20Platform%20and%20Vendor.jpeg" 
                alt="iTMunch Logo" 
                className="h-10 object-contain hover:opacity-80 transition-opacity"
                data-testid="itmunch-logo"
              />
            </a>
            <a 
              href="https://itmunch.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 hover:shadow-md"
              style={{
                background: 'linear-gradient(to right, #4AC5DB, #14b8a6)',
                color: 'white'
              }}
            >
              <Globe className="h-4 w-4" />
              Home Page
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-teal-500/10 to-cyan-600/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center">
            <Badge className="mb-4 px-4 py-1 bg-cyan-100 text-cyan-700 border-cyan-200" data-testid="hero-badge">
              Premium Link Insertion Service
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6" data-testid="hero-title">
              Boost Your SEO with
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-teal-600 mt-2" style={{background: 'linear-gradient(to right, #4AC5DB, #14b8a6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>
                iTMunch Link Insertion
              </span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto mb-8" data-testid="hero-description">
              Get high-authority backlinks from iTMunch.com - a trusted technology publication with massive reach and engagement.
            </p>
            <Button 
              size="lg" 
              className="text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-shadow"
              style={{background: 'linear-gradient(to right, #4AC5DB, #14b8a6)'}}
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-cta-button"
            >
              View Pricing
            </Button>
          </div>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="bg-white py-12 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div data-testid="metric-authority">
              <div className="flex justify-center mb-3">
                <TrendingUp className="h-8 w-8" style={{color: '#4AC5DB'}} />
              </div>
              <div className="text-3xl font-bold text-gray-900">DA 68</div>
              <div className="text-sm text-gray-600 mt-1">Domain Authority</div>
            </div>
            <div data-testid="metric-traffic">
              <div className="flex justify-center mb-3">
                <Users className="h-8 w-8 text-teal-600" />
              </div>
              <div className="text-3xl font-bold text-gray-900">500K+</div>
              <div className="text-sm text-gray-600 mt-1">Monthly Visitors</div>
            </div>
            <div data-testid="metric-backlinks">
              <div className="flex justify-center mb-3">
                <Globe className="h-8 w-8 text-cyan-600" />
              </div>
              <div className="text-3xl font-bold text-gray-900">10K+</div>
              <div className="text-sm text-gray-600 mt-1">Quality Backlinks</div>
            </div>
            <div data-testid="metric-rating">
              <div className="flex justify-center mb-3">
                <Star className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900">4.9/5</div>
              <div className="text-sm text-gray-600 mt-1">Client Rating</div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-16 bg-gradient-to-br from-slate-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="benefits-title">
              Why Choose iTMunch?
            </h2>
            <p className="text-base sm:text-lg text-gray-600">Premium benefits that drive real results</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow" data-testid="benefit-authority">
              <CardHeader>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{backgroundColor: '#E0F7FA'}}>
                  <Zap className="h-6 w-6" style={{color: '#4AC5DB'}} />
                </div>
                <CardTitle>High Authority</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  iTMunch is a well-established technology publication with DA 68, ensuring your backlinks carry significant SEO weight.
                </CardDescription>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow" data-testid="benefit-traffic">
              <CardHeader>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-teal-600" />
                </div>
                <CardTitle>Real Traffic</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Get exposure to over 500K monthly tech-savvy readers who are actively engaged with quality content.
                </CardDescription>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow" data-testid="benefit-permanent">
              <CardHeader>
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-cyan-600" />
                </div>
                <CardTitle>Permanent Links</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  All links are permanent and DoFollow, providing long-term SEO value and sustained referral traffic.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 px-4 py-1 bg-green-100 text-green-700 border-green-200" data-testid="pricing-badge">
              Limited Time Offer - 50% OFF
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="pricing-title">
              Choose Your Package
            </h2>
            <p className="text-base sm:text-lg text-gray-600">Flexible options to meet your needs</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg) => (
              <Card 
                key={pkg.id} 
                className={`border-2 relative hover:shadow-2xl transition-shadow ${
                  pkg.popular ? 'shadow-xl scale-105' : 'border-gray-200'
                }`}
                style={pkg.popular ? {borderColor: '#4AC5DB'} : {}}
                data-testid={`pricing-card-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="text-white px-4 py-1" style={{background: 'linear-gradient(to right, #4AC5DB, #14b8a6)'}}>
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="pb-4">
                  <Badge className="w-fit mb-2 bg-green-100 text-green-700 border-green-200">
                    {pkg.discount}
                  </Badge>
                  <CardTitle className="text-2xl mb-2">{pkg.title}</CardTitle>
                  <div className="flex items-baseline gap-2">
                    {pkg.originalPrice && (
                      <span className="text-2xl text-gray-400 line-through">${pkg.originalPrice}</span>
                    )}
                    <span className="text-4xl font-bold text-gray-900">
                      {typeof pkg.price === 'number' ? `$${pkg.price}` : pkg.price}
                    </span>
                    {typeof pkg.price === 'number' && <span className="text-gray-600">USD</span>}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${
                      pkg.popular 
                        ? '' 
                        : 'bg-gray-900 hover:bg-gray-800'
                    } text-white py-6 text-lg rounded-full shadow-md hover:shadow-lg transition-shadow`}
                    style={pkg.popular ? {background: 'linear-gradient(to right, #4AC5DB, #14b8a6)'} : {}}
                    onClick={() => handlePackageSelect(pkg)}
                    data-testid={`select-package-${pkg.id}`}
                  >
                    {pkg.id === 'custom' ? 'Request Quote' : 'Get Started'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-16 bg-gradient-to-br from-cyan-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="testimonials-title">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg" data-testid="testimonial-1">
              <CardHeader>
                <div className="flex gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <CardTitle className="text-lg">Outstanding Results</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  "iTMunch helped us boost our domain authority significantly. The quality of their audience and the permanent links made a huge difference in our SEO strategy."
                </CardDescription>
                <p className="text-sm text-gray-600">Marketing Director, Technology</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg" data-testid="testimonial-2">
              <CardHeader>
                <div className="flex gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <CardTitle className="text-lg">Professional Service</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  "The team at iTMunch made the entire process seamless. Our article got great visibility and the referral traffic has been consistent. Highly recommended!"
                </CardDescription>
                <p className="text-sm text-gray-600">CEO, Software Development</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg" data-testid="testimonial-3">
              <CardHeader>
                <div className="flex gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <CardTitle className="text-lg">Great ROI</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  "Best investment we made for our backlink strategy. The high DA and engaged audience on iTMunch delivered results beyond our expectations."
                </CardDescription>
                <p className="text-sm text-gray-600">SEO Manager, Digital Marketing</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Form Section */}
      {showForm && selectedPackage && (
        <div className="py-16 bg-white" id="form-section">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-2 shadow-xl" style={{borderColor: '#4AC5DB'}} data-testid="submission-form">
              <CardHeader>
                <CardTitle className="text-2xl">Complete Your Order</CardTitle>
                <CardDescription className="text-base">
                  {selectedPackage.title} - {typeof selectedPackage.price === 'number' ? `$${selectedPackage.price} USD` : 'Custom Pricing'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePayment} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      data-testid="form-input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      data-testid="form-input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      data-testid="form-input-phone"
                    />
                  </div>
                  
                  {selectedPackage.id === 'single_link' && (
                    <>
                      <div>
                        <Label htmlFor="itmunch_article">Which iTMunch Article Would You Like to Insert Your Link? *</Label>
                        <Input
                          id="itmunch_article"
                          name="itmunch_article"
                          type="url"
                          value={formData.itmunch_article}
                          onChange={handleInputChange}
                          required
                          placeholder="https://itmunch.com/article-url"
                          className="mt-1"
                          data-testid="form-input-itmunch-article"
                        />
                        <p className="text-sm text-gray-500 mt-1">Provide the iTMunch article URL where you want your link inserted</p>
                      </div>
                      <div>
                        <Label htmlFor="url">Your URL for Link Insertion *</Label>
                        <Input
                          id="url"
                          name="url"
                          type="url"
                          value={formData.url}
                          onChange={handleInputChange}
                          required
                          placeholder="https://example.com"
                          className="mt-1"
                          data-testid="form-input-url"
                        />
                        <p className="text-sm text-gray-500 mt-1">The URL you want to link to from the iTMunch article</p>
                      </div>
                    </>
                  )}
                  
                  {selectedPackage.id === 'article_three_links' && (
                    <>
                      {/* Coupon Code Section */}
                      <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-xl p-4 border border-cyan-200">
                        <div className="flex items-center gap-2 mb-3">
                          <Tag className="h-5 w-5 text-cyan-600" />
                          <h3 className="font-semibold text-gray-900">Have a Coupon Code?</h3>
                        </div>
                        
                        {!couponApplied ? (
                          <div className="flex gap-2">
                            <Input
                              placeholder="Enter coupon code"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                              className="flex-1 bg-white border-cyan-200 focus:border-cyan-400"
                              data-testid="coupon-code-input"
                            />
                            <Button
                              type="button"
                              onClick={handleApplyCoupon}
                              disabled={couponLoading || !couponCode.trim()}
                              className="bg-cyan-600 hover:bg-cyan-700 text-white px-6"
                              data-testid="apply-coupon-btn"
                            >
                              {couponLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Apply'
                              )}
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-green-200">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="h-5 w-5 text-green-600" />
                                <div>
                                  <span className="font-mono font-semibold text-green-700">{couponApplied.code}</span>
                                  <span className="ml-2 text-sm text-green-600">({couponApplied.discount_percent}% off)</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={handleRemoveCoupon}
                                className="p-1 hover:bg-red-50 rounded text-red-500 hover:text-red-600 transition-colors"
                                data-testid="remove-coupon-btn"
                              >
                                <XCircle className="h-5 w-5" />
                              </button>
                            </div>
                            
                            {/* Price breakdown */}
                            <div className="bg-white rounded-lg p-3 border border-slate-200 space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Original Price:</span>
                                <span className="text-gray-500 line-through">${selectedPackage.price.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-green-600">Discount:</span>
                                <span className="text-green-600">-${(couponApplied.discount_amount / 100).toFixed(2)}</span>
                              </div>
                              <div className="border-t border-slate-200 pt-2 flex justify-between font-semibold">
                                <span className="text-gray-900">Final Price:</span>
                                <span className="text-cyan-600 text-lg">${discountedPrice.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <Label htmlFor="article_content">Article Content or URL</Label>
                        <Textarea
                          id="article_content"
                          name="article_content"
                          value={formData.article_content}
                          onChange={handleInputChange}
                          rows={4}
                          placeholder="Paste article URL (e.g., Google Docs, Microsoft Word Online, Dropbox) or article text content"
                          className="mt-1"
                          data-testid="form-textarea-article"
                        />
                        <p className="text-sm text-gray-500 mt-1">You can share a link to your article (Google Docs, Word Online, Dropbox, etc.) or upload a file below</p>
                      </div>
                      
                      <div>
                        <Label htmlFor="article_file">Upload Article File (Optional)</Label>
                        <Input
                          id="article_file"
                          name="article_file"
                          type="file"
                          onChange={handleFileChange}
                          className="mt-1"
                          accept=".doc,.docx,.pdf,.txt"
                          data-testid="form-input-article-file"
                        />
                        <p className="text-sm text-gray-500 mt-1">Accepted formats: DOC, DOCX, PDF, TXT</p>
                      </div>

                      <div className="space-y-4 pt-2">
                        <h3 className="font-semibold text-gray-900">Your 3 Links *</h3>
                        <div>
                          <Label htmlFor="link1">Link 1 *</Label>
                          <Input
                            id="link1"
                            name="link1"
                            type="url"
                            value={formData.link1}
                            onChange={handleInputChange}
                            required
                            placeholder="https://example.com/page1"
                            className="mt-1"
                            data-testid="form-input-link1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="link2">Link 2 *</Label>
                          <Input
                            id="link2"
                            name="link2"
                            type="url"
                            value={formData.link2}
                            onChange={handleInputChange}
                            required
                            placeholder="https://example.com/page2"
                            className="mt-1"
                            data-testid="form-input-link2"
                          />
                        </div>
                        <div>
                          <Label htmlFor="link3">Link 3 *</Label>
                          <Input
                            id="link3"
                            name="link3"
                            type="url"
                            value={formData.link3}
                            onChange={handleInputChange}
                            required
                            placeholder="https://example.com/page3"
                            className="mt-1"
                            data-testid="form-input-link3"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  {selectedPackage.id === 'custom' && (
                    <div>
                      <Label htmlFor="custom_request">Describe Your Requirements *</Label>
                      <Textarea
                        id="custom_request"
                        name="custom_request"
                        value={formData.custom_request}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        placeholder="Tell us about your custom requirements"
                        className="mt-1"
                        data-testid="form-textarea-custom"
                      />
                    </div>
                  )}
                  
                  <Button 
                    type="submit" 
                    className="w-full text-white py-6 text-lg rounded-full shadow-md hover:shadow-lg transition-shadow"
                    style={{background: 'linear-gradient(to right, #4AC5DB, #14b8a6)'}}
                    disabled={loading}
                    data-testid="form-submit-button"
                  >
                    {loading ? 'Processing...' : selectedPackage.id === 'custom' ? 'Submit Request' : (
                      discountedPrice !== null 
                        ? `Pay $${discountedPrice.toFixed(2)} USD` 
                        : `Proceed to Payment ($${selectedPackage.price} USD)`
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Brand Section */}
            <div>
              <a href="https://itmunch.com/" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_editorial-connect/artifacts/r8fs74mn_iTMunch%20B2B%20Content%20Syndication%20Platform%20and%20Vendor.jpeg" 
                  alt="iTMunch Logo" 
                  className="h-12 mb-4 brightness-0 invert hover:opacity-80 transition-opacity cursor-pointer"
                />
              </a>
              <p className="text-sm text-gray-400">
                Premium link insertion services for high-authority backlinks and SEO growth.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="https://itmunch.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors font-medium"
                  >
                    <Globe className="h-4 w-4" />
                    Home Page
                  </a>
                </li>
                <li>
                  <a 
                    href="#pricing" 
                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Pricing
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="https://itmunch.com/terms-conditions/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                    data-testid="footer-terms-link"
                  >
                    Terms & Conditions
                  </a>
                </li>
                <li>
                  <a 
                    href="https://itmunch.com/privacy-policy/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                    data-testid="footer-privacy-link"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a 
                    href="https://itmunch.com/refund-policy/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                    data-testid="footer-refund-link"
                  >
                    Refund Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} iTMunch. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LinkInsertionServices;
